import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Trash2,
    Package,
    DollarSign,
    User,
    MapPin,
    Calendar,
    Clock,
    CheckCircle,
    AlertCircle,
    Loader,
    Receipt,
    CreditCard,
    X as XIcon
} from 'lucide-react';
import { purchasesAPI, receiptsAPI } from '../../services/garageApi';

const PurchaseDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [purchase, setPurchase] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [paymentError, setPaymentError] = useState('');
    const [paymentData, setPaymentData] = useState({
        amount: 0,
        payment_method: 'cash',
        receipt_date: new Date().toISOString().split('T')[0],
    });

    const fetchPurchase = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await purchasesAPI.show(id);
            const data = response.data;

            if (!data) {
                throw new Error('Purchase order not found');
            }

            setPurchase(data);

            // Set default payment amount to outstanding
            const receipts = data.receipts || [];
            const totalPaid = receipts.reduce((sum, r) => sum + Number(r.amount || 0), 0);
            const outstanding = Number(data.total_amount || 0) - totalPaid;
            setPaymentData(prev => ({
                ...prev,
                amount: outstanding > 0 ? outstanding : 0
            }));
        } catch (err) {
            console.error('Error fetching purchase:', err);
            setError(err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to load purchase order');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchPurchase();
    }, [fetchPurchase]);

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this purchase order? This will reverse inventory changes.')) return;
        try {
            await purchasesAPI.delete(id);
            alert('Purchase order deleted successfully');
            navigate('/purchases');
        } catch (err) {
            console.error('Error deleting purchase:', err);
            const message = err.response?.data?.error || err.response?.data?.message || 'Failed to delete purchase order';
            alert('Error: ' + message);
        }
    };

    const handlePayment = async (e) => {
        e.preventDefault();
        setPaymentError('');

        if (!paymentData.amount || Number(paymentData.amount) <= 0) {
            setPaymentError('Amount must be greater than 0.');
            return;
        }

        try {
            setPaymentLoading(true);
            await receiptsAPI.create({
                transaction_id: purchase.id,
                type: 'purchase',
                amount: Number(paymentData.amount),
                receipt_date: paymentData.receipt_date,
                payment_method: paymentData.payment_method,
            });
            setShowPaymentModal(false);
            fetchPurchase();
        } catch (err) {
            console.error('Error recording payment:', err);
            const message = err.response?.data?.error || err.response?.data?.message || 'Error recording payment.';
            setPaymentError(message);
        } finally {
            setPaymentLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const config = {
            pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
            paid: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
            cancelled: { color: 'bg-red-100 text-red-800', icon: AlertCircle },
        };
        return config[status] || { color: 'bg-gray-100 text-gray-800', icon: Clock };
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="flex items-center">
                    <Loader className="animate-spin h-5 w-5 mr-2" />
                    Loading purchase order...
                </div>
            </div>
        );
    }

    if (error || !purchase) {
        return (
            <div className="space-y-4">
                <div className="rounded-md bg-red-50 border border-red-200 p-4">
                    <div className="flex items-center space-x-2 text-sm text-red-700">
                        <AlertCircle className="h-4 w-4" />
                        <span>{error || 'Purchase order not found'}</span>
                    </div>
                </div>
                <button
                    onClick={() => navigate('/purchases')}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Purchases
                </button>
            </div>
        );
    }

    const badge = getStatusBadge(purchase.status);
    const BadgeIcon = badge.icon;
    const purchaseItems = purchase.purchase_items || purchase.purchaseItems || [];
    const receipts = purchase.receipts || [];
    const totalPaid = receipts.reduce((sum, r) => sum + Number(r.amount || 0), 0);
    const outstandingAmount = Number(purchase.total_amount || 0) - totalPaid;

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <button
                        onClick={() => navigate('/purchases')}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Purchase {purchase.id}</h1>
                        <p className="text-gray-600">
                            {purchase.purchase_date ? new Date(purchase.purchase_date).toLocaleDateString() : ''}
                        </p>
                    </div>
                </div>

                <div className="flex items-center space-x-3">
                    <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
                        <BadgeIcon className="h-4 w-4" />
                        <span className="capitalize">{purchase.status}</span>
                    </span>

                    {purchase.status !== 'paid' && (
                        <div className="flex space-x-2">
                            {outstandingAmount > 0 && (
                                <button
                                    onClick={() => setShowPaymentModal(true)}
                                    className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                                >
                                    <DollarSign className="h-4 w-4 mr-1" />
                                    Record Payment
                                </button>
                            )}
                            <button
                                onClick={handleDelete}
                                className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                            >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Delete
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Purchase Info */}
            <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Supplier */}
                        <div>
                            <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                                <User className="h-4 w-4 mr-2" />
                                Supplier
                            </h3>
                            {purchase.supplier ? (
                                <div className="text-sm text-gray-600 space-y-1">
                                    <div className="font-medium text-gray-900">{purchase.supplier.name}</div>
                                    {purchase.supplier.email && (
                                        <div>{purchase.supplier.email}</div>
                                    )}
                                    {purchase.supplier.phone && (
                                        <div>{purchase.supplier.phone}</div>
                                    )}
                                    {purchase.supplier.address && (
                                        <div>{purchase.supplier.address}</div>
                                    )}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500">No supplier info</p>
                            )}
                        </div>

                        {/* Purchase Details */}
                        <div>
                            <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                                <Package className="h-4 w-4 mr-2" />
                                Order Details
                            </h3>
                            <div className="text-sm text-gray-600 space-y-2">
                                <div className="flex justify-between">
                                    <span className="flex items-center">
                                        <Calendar className="h-3 w-3 mr-1" />
                                        Purchase Date:
                                    </span>
                                    <span className="font-medium">
                                        {purchase.purchase_date ? new Date(purchase.purchase_date).toLocaleDateString() : '-'}
                                    </span>
                                </div>
                                {purchase.location && (
                                    <div className="flex justify-between">
                                        <span className="flex items-center">
                                            <MapPin className="h-3 w-3 mr-1" />
                                            Location:
                                        </span>
                                        <span className="font-medium">{purchase.location.name}</span>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <span>Total:</span>
                                    <span className="font-bold text-lg text-gray-900">
                                        ${Number(purchase.total_amount || 0).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Purchase Items */}
                <div className="px-6 py-4">
                    <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
                        <Package className="h-4 w-4 mr-2" />
                        Items ({purchaseItems.length})
                    </h3>

                    {purchaseItems.length > 0 ? (
                        <div className="overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost Price</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {purchaseItems.map((item, index) => (
                                        <tr key={item.id || index} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                {item.purchaseable?.name || item.purchaseable?.battery_name || item.purchaseable?.size || `Item #${index + 1}`}
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs capitalize">
                                                    {item.purchaseable_type || '-'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.quantity}</td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                                ${Number(item.cost_price_at_purchase || 0).toFixed(2)}
                                            </td>
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                                                ${Number(item.total_price || 0).toFixed(2)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-gray-50">
                                    <tr>
                                        <td colSpan={4} className="px-4 py-3 text-sm font-medium text-gray-700 text-right">
                                            Total:
                                        </td>
                                        <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                                            ${Number(purchase.total_amount || 0).toFixed(2)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                            <p>No items found</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Payment Information */}
            <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-sm font-medium text-gray-900 flex items-center">
                        <Receipt className="h-4 w-4 mr-2" />
                        Payments ({receipts.length})
                    </h3>
                </div>

                {/* Payment Summary Cards */}
                <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-500">Total Amount</p>
                        <p className="text-xl font-bold text-gray-900">${Number(purchase.total_amount || 0).toFixed(2)}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-500">Paid Amount</p>
                        <p className="text-xl font-bold text-green-600">${totalPaid.toFixed(2)}</p>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-500">Outstanding</p>
                        <p className="text-xl font-bold text-red-600">${outstandingAmount.toFixed(2)}</p>
                    </div>
                </div>

                {/* Payment History */}
                {receipts.length > 0 ? (
                    <div className="px-6 pb-4">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt ID</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {receipts.map((receipt) => (
                                    <tr
                                        key={receipt.id}
                                        className="hover:bg-gray-50 cursor-pointer"
                                        onClick={() => navigate(`/receipts/${receipt.id}`)}
                                    >
                                        <td className="px-4 py-3 text-sm font-medium text-blue-600">{receipt.id}</td>
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                            {receipt.receipt_date ? new Date(receipt.receipt_date).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600 capitalize">{receipt.payment_method || '-'}</td>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                                            ${Number(receipt.amount || 0).toFixed(2)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-gray-50">
                                <tr>
                                    <td colSpan={3} className="px-4 py-3 text-sm font-medium text-gray-700 text-right">
                                        Total Paid:
                                    </td>
                                    <td className="px-4 py-3 text-sm font-bold text-green-600 text-right">
                                        ${totalPaid.toFixed(2)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                ) : (
                    <div className="px-6 py-8 text-center text-gray-500">
                        <DollarSign className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                        <p>No payments recorded yet.</p>
                        {purchase.status !== 'paid' && outstandingAmount > 0 && (
                            <button
                                onClick={() => setShowPaymentModal(true)}
                                className="mt-3 inline-flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                            >
                                <DollarSign className="h-4 w-4 mr-1" />
                                Record Payment
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Payment Modal */}
            {showPaymentModal && (
                <div className="fixed inset-0 z-50 bg-gray-600 bg-opacity-50 flex items-center justify-center">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold flex items-center">
                                <CreditCard className="h-5 w-5 mr-2 text-green-600" />
                                Record Payment
                            </h3>
                            <button
                                onClick={() => { setShowPaymentModal(false); setPaymentError(''); }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <XIcon className="h-5 w-5" />
                            </button>
                        </div>

                        <p className="text-sm text-gray-500 mb-4">
                            Outstanding: <span className="font-semibold text-red-600">${outstandingAmount.toFixed(2)}</span>
                        </p>

                        {paymentError && (
                            <div className="mb-4 rounded-md bg-red-50 border border-red-200 p-3">
                                <div className="flex items-center space-x-2 text-sm text-red-700">
                                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                    <span>{paymentError}</span>
                                </div>
                            </div>
                        )}

                        <form onSubmit={handlePayment} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                                <input
                                    type="number"
                                    value={paymentData.amount}
                                    onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                                    className="form-input w-full rounded-md border-gray-300"
                                    step="0.01"
                                    min="0.01"
                                    max={outstandingAmount}
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Receipt Date *</label>
                                <input
                                    type="date"
                                    value={paymentData.receipt_date}
                                    onChange={(e) => setPaymentData(prev => ({ ...prev, receipt_date: e.target.value }))}
                                    className="form-input w-full rounded-md border-gray-300"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
                                <select
                                    value={paymentData.payment_method}
                                    onChange={(e) => setPaymentData(prev => ({ ...prev, payment_method: e.target.value }))}
                                    className="form-select w-full rounded-md border-gray-300"
                                    required
                                >
                                    <option value="cash">Cash</option>
                                    <option value="cheque">Cheque</option>
                                    <option value="credit_card">Credit Card</option>
                                    <option value="bank_transfer">Bank Transfer</option>
                                </select>
                            </div>

                            <div className="flex justify-end space-x-3 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={() => { setShowPaymentModal(false); setPaymentError(''); }}
                                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={paymentLoading}
                                >
                                    {paymentLoading ? 'Processing...' : 'Record Payment'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PurchaseDetail;
